export type FastCheckoutMethod = 'applePay' | 'googlePay' | 'payPal'
export type FastCheckoutSurface = 'pdp' | 'layer'

const METHODS_STATE_KEY = 'pdp-fast-checkout-methods'

const isPayPalExpress = (paymentMethod: {
  metadata?: { ppExpress?: unknown } | null
}) => String(paymentMethod.metadata?.ppExpress) === 'true'

/**
 * Lightweight fast checkout availability detection.
 *
 * The wallet components (`CheckoutApplePayPayment`, `CheckoutGooglePayPayment`,
 * `CartPaypalExpressButton`) each resolve their own availability while mounting
 * the Adyen/PayPal SDKs. That is fine in the minicart, where the buttons are
 * simply stacked, but the PDP needs to know *how many* methods are available
 * before deciding which layout to render — including before a size (SKU) is
 * selected, and including when the guest has no cart yet.
 *
 * Apple Pay / Google Pay use cheap device checks. PayPal Express is read from
 * `currentMarketSettings.isPaypalExpressEnabled` (baked at market-settings
 * generate) so a guest PDP does not list payment methods from Commerce Layer.
 * If a cart is already loaded, its `available_payment_methods` win.
 *
 * @returns Available methods, count, and whether client detection has finished.
 */
export const useFastCheckoutMethods = () => {
  const { cart } = useCart()
  const { getPaypalExpressPaymentMethodId } = usePaypalExpress()
  const configs = useConfigs()
  const appConfig = useAppConfig()

  /** Null until the client-side detection has run, so callers can hold a placeholder. */
  const availableMethods = useState<FastCheckoutMethod[] | null>(
    METHODS_STATE_KEY,
    () => null
  )

  const isResolved = computed(() => availableMethods.value !== null)
  const methodsCount = computed(() => availableMethods.value?.length ?? 0)

  /**
   * Apple Pay is offered by Safari/iOS only, and only when the device has an
   * active payment card. `canMakePayments` is synchronous and does not prompt.
   */
  const canUseApplePay = () => {
    if (!import.meta.client) return false
    const applePaySession = (
      window as unknown as {
        ApplePaySession?: { canMakePayments?: () => boolean }
      }
    ).ApplePaySession
    return applePaySession?.canMakePayments?.() === true
  }

  /**
   * Google Pay is offered when the browser exposes the Payment Request API.
   * Adyen is required later on the express order; gating the visual CTA on the
   * (often missing) cart hid the buttons for guests.
   */
  const canUseGooglePay = () => {
    if (!import.meta.client) return false
    return typeof window.PaymentRequest === 'function'
  }

  /**
   * `null` when the cart has not exposed payment methods yet — caller should
   * fall back to market settings (or a one-off CL listing if the flag is absent).
   */
  const hasPayPalOnCart = (): boolean | null => {
    const paymentMethods = cart.value?.available_payment_methods
    if (!paymentMethods) return null
    return paymentMethods.some(isPayPalExpress)
  }

  const hasPayPalInMarketSettings = (): boolean | null => {
    const flag = appConfig.currentMarketSettings?.isPaypalExpressEnabled
    return typeof flag === 'boolean' ? flag : null
  }

  const loadPayPalFromApi = async () => {
    try {
      const paymentMethodId = await getPaypalExpressPaymentMethodId(
        cart.value?.market?.id,
        configs.value.cl_market_code
      )
      return !!paymentMethodId
    } catch {
      return false
    }
  }

  const detectMethods = async () => {
    if (!import.meta.client) return

    const payPalOnCart = hasPayPalOnCart()
    const payPalFromSettings = hasPayPalInMarketSettings()
    const payPal =
      payPalOnCart === true ||
      (payPalOnCart === null &&
        (payPalFromSettings ?? (await loadPayPalFromApi())))

    const methods: FastCheckoutMethod[] = []
    if (payPal) methods.push('payPal')
    if (canUseApplePay()) methods.push('applePay')
    if (canUseGooglePay()) methods.push('googlePay')

    if (availableMethods.value?.join() === methods.join()) return
    availableMethods.value = methods
  }

  watch(
    () => [cart.value?.id, cart.value?.available_payment_methods],
    () => {
      detectMethods()
    },
    { immediate: true }
  )

  return {
    availableMethods,
    methodsCount,
    isResolved,
  }
}
